// AdminScreen - 운영 콘솔 (`/admin`).
//
// 담당자용 앱과 청중이 다르다. 담당자는 자기 회사의 개발품을 보고, 운영자는
// **전 테넌트의 원본 데이터**를 본다. 그래서 셸(사이드바·검색)을 쓰지 않고
// 별도 경로에 단독으로 선다.
//
// 데이터는 조회 전용이다(`api/routers/superadmin.py`) - 고쳐야 하면 그 유저로
// 임퍼소네이션해서 앱 본체의 정규 경로로 고친다. 운영 콘솔에 우회로를 만들면
// 감사 추적이 끊긴다.
//
// 예외는 **계정 생성** 하나다. 앱 본체에는 계정을 만드는 화면이 없고(가입은
// Firebase 가, 등록은 최초 로그인이 한다), 시연·교육 계정은 사람이 브라우저로
// 로그인해야 하므로 운영자만 쓰는 이 화면이 유일한 자리다.
//
// 표시는 wire(snake_case) 원본 그대로다. camelCase 로 매핑하면 "화면이 보여준
// 것"과 "DB 에 있는 것"이 달라져 원본 뷰어의 목적이 사라진다.
import { BarTrend } from "@/components/charts";
import { KpiCard } from "@/components/KpiCard";
import { Table, TableScroll, Td, Th, THead, Tr } from "@/components/Table";
import {
  Button,
  Card,
  Checkbox,
  DataBoundary,
  EmptyState,
  Field,
  Icon,
  Pagination,
  SectionTitle,
  Select,
  TextInput,
} from "@/components/ui";
import {
  type ActivityReport,
  type ProvisionedAccount,
  type SuperadminUser,
  type UserRole,
  getSuperadminActivity,
  getSuperadminCollection,
  getSuperadminUsers,
  mintImpersonationToken,
  provisionAccount,
  setImpersonation,
} from "@/lib/api";
import { ACTIVITY_RANGES, activeToday, avgPerDay, toBars } from "@/lib/activity";
import { EMPTY, fmt } from "@/lib/format";
import { useResource } from "@/lib/hooks";
import { useCallback, useState } from "react";

/** 서버 `_LISTERS` allowlist 와 같은 순서. 없는 이름을 부르면 404 다. */
const COLLECTIONS = [
  "dev_items",
  "milestones",
  "prep_items",
  "production_plans",
  "process_routes",
  "issues",
  "staff",
  "customers",
  "process_masters",
  "lead_time_templates",
  "holidays",
  "company_settings",
] as const;

/** `/users` 요약 카드가 세는 키 (서버 `_COUNT_KEYS`). */
const COUNT_KEYS = ["dev_items", "production_plans", "issues", "staff", "customers"] as const;

const PAGE_SIZE = 50;

/** 원본 값 렌더. 객체·배열은 JSON 으로 접어서 한 줄에 둔다. */
const cell = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const CollectionBrowser = ({ user }: { user: SuperadminUser }) => {
  const [name, setName] = useState<string>(COLLECTIONS[0]);
  const [page, setPage] = useState(1);

  const fetcher = useCallback(
    () => getSuperadminCollection(user.uid, name, (page - 1) * PAGE_SIZE, PAGE_SIZE),
    [user.uid, name, page]
  );
  const res = useResource(fetcher, { total: 0, items: [] });

  // 열은 응답에서 유도한다 - 컬렉션마다 필드가 달라 고정할 수 없다.
  const columns = res.data.items.length > 0 ? Object.keys(res.data.items[0]) : [];
  const pageCount = Math.ceil(res.data.total / PAGE_SIZE);

  return (
    <Card>
      <div className="flex flex-col gap-s4">
        <div className="flex flex-wrap items-center gap-s2">
          {COLLECTIONS.map((c) => (
            <Button
              key={c}
              size="sm"
              variant={c === name ? "primary" : "secondary"}
              onClick={() => {
                setName(c);
                setPage(1);
              }}
            >
              {c}
            </Button>
          ))}
        </div>

        <DataBoundary loading={res.loading} error={res.error} onRetry={() => void res.reload()}>
          {res.data.items.length === 0 ? (
            <EmptyState
              icon="db"
              title="문서가 없습니다"
              description={`${user.name} 의 회사에는 ${name} 문서가 아직 없어요.`}
            />
          ) : (
            <>
              <p className="text-caption" style={{ color: "var(--text-tertiary)" }}>
                {name} · 전체 {res.data.total}건
              </p>
              <TableScroll>
                <Table label={`${user.name} 의 ${name}`}>
                  <THead>
                    <tr>
                      {columns.map((c) => (
                        <Th key={c}>{c}</Th>
                      ))}
                    </tr>
                  </THead>
                  <tbody>
                    {res.data.items.map((row, i) => (
                      <Tr key={cell(row.id ?? i)}>
                        {columns.map((c) => (
                          <Td key={c}>{cell(row[c])}</Td>
                        ))}
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
              <Pagination page={page} pageCount={pageCount} onChange={setPage} />
            </>
          )}
        </DataBoundary>
      </div>
    </Card>
  );
};

const EMPTY_ACTIVITY: ActivityReport = {
  days: 0,
  since: "",
  until: "",
  total_requests: 0,
  active_users: 0,
  daily: [],
  users: [],
};

/**
 * 사용자 활동 지표 - 누가 얼마나 쓰는가.
 *
 * 원천은 인증 요청 카운터 하나다(서버 `user_activity`). 화면 단위 추적이
 * 아니라 "붙어 있었나"의 지표이므로, 요청 수는 절대값보다 유저 간 비교와
 * 추세로 읽는다. 한 번도 접속하지 않은 유저가 0 으로 함께 보이는 것이
 * 이 표의 목적이다 - 배포했는데 아무도 안 쓰는 상태를 여기서 발견한다.
 */
const ActivityPanel = () => {
  const [days, setDays] = useState<number>(14);
  const fetcher = useCallback(() => getSuperadminActivity(days), [days]);
  const res = useResource(fetcher, EMPTY_ACTIVITY);
  const { daily, users, total_requests, active_users } = res.data;

  return (
    <Card>
      <div className="flex flex-col gap-s4">
        <div className="flex flex-wrap items-center gap-s3">
          <SectionTitle sub="인증 요청 기준 (KST) · 임퍼소네이션 호출은 제외됩니다">
            사용자 활동
          </SectionTitle>
          <span className="flex-1" />
          <span className="flex gap-s2">
            {ACTIVITY_RANGES.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={d === days ? "primary" : "secondary"}
                onClick={() => setDays(d)}
              >
                {d}일
              </Button>
            ))}
          </span>
        </div>

        <DataBoundary loading={res.loading} error={res.error} onRetry={() => void res.reload()}>
          <div className="flex flex-col gap-s4">
            <div className="grid gap-s3 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label={`활성 사용자 (${days}일)`}
                value={active_users}
                unit="명"
                emphasis="anchor"
                hint={`전체 ${users.length}명 중`}
              />
              <KpiCard label="오늘 접속" value={activeToday(daily)} unit="명" />
              <KpiCard label="총 요청" value={total_requests} unit="건" />
              <KpiCard
                label="일평균 요청"
                value={avgPerDay(total_requests, daily.length)}
                unit="건"
              />
            </div>

            {total_requests === 0 ? (
              <EmptyState
                icon="users"
                title="활동 기록이 없습니다"
                description={`최근 ${days}일 동안 로그인한 사용자가 없어요.`}
              />
            ) : (
              <>
                <TableScroll>
                  <div style={{ minWidth: daily.length * 34 }}>
                    <BarTrend data={toBars(daily)} height={160} />
                  </div>
                </TableScroll>
                <p className="text-caption" style={{ color: "var(--text-tertiary)" }}>
                  막대 = 요청 수, 진한 부분 = 그날 접속한 사용자 수
                </p>
              </>
            )}

            <TableScroll>
              <Table label="사용자별 활동">
                <THead>
                  <tr>
                    <Th>이메일</Th>
                    <Th>이름</Th>
                    <Th>권한</Th>
                    <Th>마지막 접속</Th>
                    <Th num>활동일수</Th>
                    <Th num>요청</Th>
                  </tr>
                </THead>
                <tbody>
                  {users.map((u) => (
                    <Tr key={u.uid}>
                      <Td strong>{u.email}</Td>
                      <Td>{u.name || EMPTY}</Td>
                      <Td>{u.role}</Td>
                      <Td>{u.last_seen_at || EMPTY}</Td>
                      <Td num>{`${u.active_days} / ${days}`}</Td>
                      <Td num>{fmt(u.requests)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          </div>
        </DataBoundary>
      </div>
    </Card>
  );
};

/**
 * 계정 생성 - 이메일 / 비밀번호. 서버가 Firebase 계정과 앱 유저를 함께 만든다.
 *
 * `데모 데이터 함께 생성` 은 그 계정의 회사에 개발품·마스터 1회분을 심는다.
 * 자동 시드(`DEMO_SEED`)와 별개다 - 자동 시드를 켜면 이후 **모든** 신규 로그인이
 * 데모 데이터를 받으므로, 운영에서는 이렇게 한 번만 명시적으로 넣는다.
 */
const AccountCreator = ({ onCreated }: { onCreated: () => void }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("member");
  const [seedDemo, setSeedDemo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ProvisionedAccount | null>(null);

  const submit = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const created = await provisionAccount({
        email: email.trim(),
        password,
        name: name.trim(),
        role,
        seed_demo: seedDemo,
      });
      setResult(created);
      setPassword("");
      onCreated();
    } catch (e) {
      setError(
        e instanceof Error && /404/.test(e.message)
          ? "계정 생성이 비활성 상태입니다 (SUPERADMIN_KEY 미설정)."
          : e instanceof Error
            ? e.message
            : "계정을 만들지 못했어요."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-s4">
        <SectionTitle sub="이메일 / 비밀번호 계정을 만들고 앱 유저로 등록합니다. 같은 이메일을 다시 넣으면 기존 계정을 그대로 씁니다 - 비밀번호는 덮어쓰지 않습니다.">
          계정 생성
        </SectionTitle>

        <div className="grid gap-s4 sm:grid-cols-2">
          <Field label="이메일" required>
            {(p) => (
              <TextInput type="email" value={email} onChange={setEmail} placeholder="name@company.com" {...p} />
            )}
          </Field>
          <Field label="비밀번호" required hint="6자 이상">
            {(p) => <TextInput type="password" value={password} onChange={setPassword} {...p} />}
          </Field>
          <Field label="이름" hint="비우면 이메일 로컬파트를 씁니다">
            {(p) => <TextInput value={name} onChange={setName} {...p} />}
          </Field>
          <Field label="권한">
            {(p) => (
              <Select
                value={role}
                onChange={(v) => setRole(v as UserRole)}
                options={[
                  { value: "member", label: "member" },
                  { value: "admin", label: "admin" },
                ]}
                {...p}
              />
            )}
          </Field>
        </div>

        <Checkbox
          checked={seedDemo}
          onChange={setSeedDemo}
          label="데모 데이터 함께 생성 (개발품·마일스톤·마스터 1회분)"
        />

        <div className="flex items-center gap-s3">
          <Button
            variant="primary"
            loading={busy}
            disabled={!email.trim() || password.length < 6}
            onClick={() => void submit()}
          >
            계정 생성
          </Button>
          {error && (
            <span className="flex items-center gap-s2 text-label" style={{ color: "var(--state-late)" }}>
              <Icon name="alert" size={14} />
              {error}
            </span>
          )}
        </div>

        {result && (
          <output className="text-label" style={{ color: "var(--text-secondary)" }}>
            {result.created ? "계정을 만들었습니다" : "이미 있는 계정을 연결했습니다"} · {result.email} ·{" "}
            {result.role} · 담당자 {result.staff_id ?? "미연결"}
            {result.seeded ? " · 데모 데이터 생성됨" : ""}
          </output>
        )}
      </div>
    </Card>
  );
};

export const AdminScreen = ({ onExit }: { onExit: () => void }) => {
  const users = useResource(getSuperadminUsers, [] as SuperadminUser[]);
  const [selected, setSelected] = useState<SuperadminUser | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [error, setError] = useState("");

  // 임퍼소네이션은 토큰을 저장하고 앱 본체로 나간다. 새로고침해야 AuthContext
  // 와 캐시가 전부 그 유저 기준으로 다시 선다 - 부분 갱신은 두 유저의 상태가
  // 섞인다.
  const impersonate = async (u: SuperadminUser) => {
    setBusyUid(u.uid);
    setError("");
    try {
      const t = await mintImpersonationToken(u.uid);
      setImpersonation({ token: t.token, uid: t.uid, email: t.email, name: t.name });
      window.location.assign("/");
    } catch (e) {
      setError(
        e instanceof Error && /404/.test(e.message)
          ? "임퍼소네이션이 비활성 상태입니다 (SUPERADMIN_KEY 미설정)."
          : "임퍼소네이션 토큰을 발급하지 못했어요."
      );
      setBusyUid(null);
    }
  };

  return (
    <main
      className="min-h-[100dvh] px-s4 py-s5 sm:px-s5"
      style={{ background: "var(--surface-sunken)" }}
    >
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-s5">
        <header className="flex flex-wrap items-center gap-s3">
          <div className="flex flex-col gap-s1">
            <h1 className="text-display">운영 콘솔</h1>
            <p className="text-label" style={{ color: "var(--text-tertiary)" }}>
              전 테넌트 원본 데이터 · 조회 전용
            </p>
          </div>
          <span className="flex-1" />
          <Button variant="secondary" size="md" icon={<Icon name="chevL" size={14} />} onClick={onExit}>
            앱으로 돌아가기
          </Button>
        </header>

        {error && (
          <p className="flex items-center gap-s2 text-label" style={{ color: "var(--state-late)" }}>
            <Icon name="alert" size={14} />
            {error}
          </p>
        )}

        <ActivityPanel />

        <AccountCreator onCreated={() => void users.reload()} />

        <Card>
          <DataBoundary
            loading={users.loading}
            error={users.error}
            onRetry={() => void users.reload()}
          >
            {users.data.length === 0 ? (
              <EmptyState
                icon="users"
                title="유저가 없습니다"
                description="최초 로그인이 일어나면 유저가 생성됩니다."
              />
            ) : (
              <TableScroll>
                <Table label="전체 유저">
                  <THead>
                    <tr>
                      <Th>이메일</Th>
                      <Th>이름</Th>
                      <Th>권한</Th>
                      <Th>소속</Th>
                      {COUNT_KEYS.map((k) => (
                        <Th key={k} num>
                          {k}
                        </Th>
                      ))}
                      <Th>작업</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {users.data.map((u) => (
                      <Tr key={u.uid} selected={selected?.uid === u.uid}>
                        <Td strong>{u.email}</Td>
                        <Td>{u.name || "—"}</Td>
                        <Td>{u.role}</Td>
                        <Td>{[u.dept, u.position].filter(Boolean).join(" · ") || "—"}</Td>
                        {COUNT_KEYS.map((k) => (
                          <Td key={k} num>
                            {u.counts[k] ?? 0}
                          </Td>
                        ))}
                        <Td>
                          <span className="flex gap-s2">
                            <Button
                              size="sm"
                              variant={selected?.uid === u.uid ? "primary" : "secondary"}
                              onClick={() => setSelected(selected?.uid === u.uid ? null : u)}
                            >
                              데이터
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={busyUid === u.uid}
                              onClick={() => void impersonate(u)}
                            >
                              이 사용자로 로그인
                            </Button>
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </DataBoundary>
        </Card>

        {selected && <CollectionBrowser user={selected} />}
      </div>
    </main>
  );
};
